/**
 * Base React Hooks for API operations
 * Provides foundational query and mutation hooks
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { UseQueryResult, UseMutationState } from './types';

/**
 * Base query hook for GET requests
 * @param queryFn - Async function that fetches data
 * @param options - Configuration options
 */
export function useQuery<T>(
  queryFn: () => Promise<T>,
  options: {
    enabled?: boolean;
    onSuccess?: (data: T) => void;
    onError?: (error: Error) => void;
  } = {}
): UseQueryResult<T> {
  const { enabled = true, onSuccess, onError } = options;
  
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);

  // Use refs to store callbacks to avoid recreating fetchData on every render
  const onSuccessRef = useRef(onSuccess);
  const onErrorRef = useRef(onError);
  const queryFnRef = useRef(queryFn);
  const enabledRef = useRef(enabled);
  const isMountedRef = useRef(true);

  // Update refs when values change (but don't trigger re-renders)
  useEffect(() => {
    onSuccessRef.current = onSuccess;
    onErrorRef.current = onError;
    enabledRef.current = enabled;
  }, [onSuccess, onError, enabled]);

  // Track if queryFn has changed and update ref
  const prevQueryFnRef = useRef(queryFn);
  const [queryFnVersion, setQueryFnVersion] = useState(0);
  
  // Update queryFn ref and increment version when it changes
  useEffect(() => {
    if (prevQueryFnRef.current !== queryFn) {
      queryFnRef.current = queryFn;
      prevQueryFnRef.current = queryFn;
      setQueryFnVersion(v => v + 1);
    }
  }, [queryFn]);

  const fetchData = useCallback(async () => {
    if (!enabledRef.current || !isMountedRef.current) return;

    setLoading(true);
    setError(null);

    try {
      const result = await queryFnRef.current();
      if (isMountedRef.current) {
        setData(result);
        onSuccessRef.current?.(result);
      }
    } catch (err) {
      if (isMountedRef.current) {
        const error = err instanceof Error ? err : new Error('An error occurred');
        setError(error);
        onErrorRef.current?.(error);
      }
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  }, []); // Empty deps - fetchData never changes

  // Refetch when queryFn version or enabled changes
  useEffect(() => {
    isMountedRef.current = true;
    if (enabled) {
      fetchData();
    }
    return () => {
      isMountedRef.current = false;
    };
  }, [queryFnVersion, enabled, fetchData]);

  return {
    data,
    loading,
    error,
    refetch: fetchData,
  };
}

/**
 * Base mutation hook for POST, PUT, DELETE requests
 * @param mutationFn - Async function that performs the mutation
 * @param options - Configuration options
 */
export function useMutation<TData, TVariables>(
  mutationFn: (variables: TVariables) => Promise<TData>,
  options: {
    onSuccess?: (data: TData, variables: TVariables) => void;
    onError?: (error: Error, variables: TVariables) => void;
  } = {}
): UseMutationState<TData, TVariables> {
  const { onSuccess, onError } = options;
  
  const [data, setData] = useState<TData | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);

  const mutate = useCallback(
    async (variables: TVariables): Promise<TData> => {
      setLoading(true);
      setError(null);

      try {
        const result = await mutationFn(variables);
        setData(result);
        onSuccess?.(result, variables);
        return result;
      } catch (err) {
        const error = err instanceof Error ? err : new Error('An error occurred');
        setError(error);
        onError?.(error, variables);
        throw error;
      } finally {
        setLoading(false);
      }
    },
    [mutationFn, onSuccess, onError]
  );

  const reset = useCallback(() => {
    setData(null);
    setError(null);
    setLoading(false);
  }, []);

  return {
    data,
    loading,
    error,
    mutate,
    reset,
  };
}
